import type { QueueItemDetail, Role } from '@rp/contracts'
import { Button } from '../../design'
import { formatTimeOfDay } from '../../lib/time'
import { canTakeOver } from '../../session/roles'
import { ITEM_COPY } from './copy'

export type ClaimStateKind = 'unclaimed' | 'mine' | 'others'

export const claimExpired = (item: { claim: QueueItemDetail['claim'] }, now: Date): boolean =>
  item.claim !== null && Date.parse(item.claim.expiresAt) <= now.getTime()

export const claimState = (item: QueueItemDetail, currentUserId: string, now: Date): ClaimStateKind => {
  if (item.claim === null || claimExpired(item, now)) return 'unclaimed'
  return item.claim.userId === currentUserId ? 'mine' : 'others'
}

type ActionProps = { pending: boolean; onActivate: () => void }

const ClaimExpiry = ({ expiresAt }: { expiresAt: string }) => (
  <p className="claim-expiry num">{ITEM_COPY.claimExpiresAt(formatTimeOfDay(new Date(expiresAt)))}</p>
)

const ClaimAction = ({ pending, onActivate, label, variant }: ActionProps & { label: string; variant?: 'primary' }) => (
  <Button
    className="claim-action"
    {...(variant === undefined ? {} : { variant })}
    pending={pending}
    pendingLabel={ITEM_COPY.claimPending}
    onClick={onActivate}
  >
    {label}
  </Button>
)

const Unclaimed = ({ pending, onActivate, wasExpired }: ActionProps & { wasExpired: boolean }) => (
  <>
    {wasExpired ? (
      <p className="claim-state" role="status">
        {ITEM_COPY.claimExpired}
      </p>
    ) : null}
    <ClaimAction pending={pending} onActivate={onActivate} label={ITEM_COPY.claim} variant="primary" />
  </>
)

const Mine = ({ pending, onActivate, expiresAt }: ActionProps & { expiresAt: string }) => (
  <>
    <p className="claim-state" role="status">
      {ITEM_COPY.claimedByMe}
    </p>
    <ClaimExpiry expiresAt={expiresAt} />
    <ClaimAction pending={pending} onActivate={onActivate} label={ITEM_COPY.release} />
  </>
)

const Others = ({ pending, onActivate, claim, role }: ActionProps & { claim: NonNullable<QueueItemDetail['claim']>; role: Role }) => (
  <>
    <span className="claim-avatar" aria-hidden="true">
      {claim.displayName.slice(0, 1)}
    </span>
    <p className="claim-state" role="status">
      {ITEM_COPY.claimedByOther(claim.displayName)}
    </p>
    <ClaimExpiry expiresAt={claim.expiresAt} />
    {canTakeOver(role) ? <ClaimAction pending={pending} onActivate={onActivate} label={ITEM_COPY.takeover} /> : null}
  </>
)

export const ClaimBar = ({
  item,
  currentUserId,
  role,
  now,
  onClaim,
  onRelease,
  pending,
  error,
}: {
  item: QueueItemDetail
  currentUserId: string
  role: Role
  now: Date
  onClaim: (options: { takeover: boolean }) => void
  onRelease: () => void
  pending: boolean
  error: string | null
}) => {
  const state = claimState(item, currentUserId, now)

  return (
    <div className={`claim-bar claim-bar-${state}`}>
      {state === 'unclaimed' ? (
        <Unclaimed pending={pending} onActivate={() => onClaim({ takeover: false })} wasExpired={item.claim !== null} />
      ) : null}
      {state === 'mine' ? <Mine pending={pending} onActivate={onRelease} expiresAt={item.claim!.expiresAt} /> : null}
      {state === 'others' ? (
        <Others pending={pending} onActivate={() => onClaim({ takeover: true })} claim={item.claim!} role={role} />
      ) : null}
      {error === null ? null : (
        <p className="claim-error tone-danger" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}
